// Shared constants for Whyte Realty

export const REGIONS = [
  "The Bronx",
  "Manhattan",
  "Yonkers",
  "Mount Vernon",
  "New Rochelle",
  "Pelham / Eastchester",
  "White Plains",
  "Bronxville",
  "Westchester",
  "Queens",
  "Brooklyn",
  "Staten Island",
  "Rockland",
  "Putnam",
  "Orange",
  "Middletown",
  "Poughkeepsie",
];

export const PROPERTY_TYPES = ["Condo", "Co-op", "Apartment", "Single Family", "Townhouse", "Multi-Family", "Other"];

// Rental hunters see Apartment / Multi-Family first — that's the honest inventory
// mix in NYC. Sale hunters usually reach for Condo / Co-op / Single Family first.
export const PROPERTY_TYPES_FOR_RENT = ["Apartment", "Multi-Family", "Condo", "Co-op", "Single Family", "Townhouse", "Other"];

export function propertyTypesForListingType(listingType) {
  return listingType === "rent" ? PROPERTY_TYPES_FOR_RENT : PROPERTY_TYPES;
}

// Only two tabs — "Buy & Rent" was removed because it conflated two very
// different shopping intents and made every filter (price, home type) noisy.
export const LISTING_TYPES = [
  { value: "sale", label: "For Sale" },
  { value: "rent", label: "For Rent" },
];

// Soft neighborhood-safety filter (flood only — crime is context-only per FH rules).
export const FLOOD_RISK_FILTERS = [
  { value: "any", label: "Any risk" },
  { value: "low", label: "Lower flood risk" },
];

export const SCHOOL_DISTRICTS = Array.from({ length: 32 }, (_, i) => `District ${i + 1}`);

export const FLOOD_LABEL = {
  low: "Low flood",
  medium: "Mod. flood",
  high: "High flood",
  unknown: "Flood N/A",
};

export const FLOOD_BADGE_CLASS = {
  low: "border-emerald-400/30 text-emerald-200 bg-emerald-500/10",
  medium: "border-amber-400/30 text-amber-200 bg-amber-500/10",
  high: "border-red-400/40 text-red-200 bg-red-500/10",
  unknown: "border-white/15 text-white/50 bg-white/5",
};

export const WALK_LABEL = { high: "Very walkable", medium: "Walkable", low: "Car-friendly" };
export const WALK_BADGE_CLASS = {
  high: "border-emerald-400/30 text-emerald-200 bg-emerald-500/10",
  medium: "border-brand/30 text-brand bg-brand/10",
  low: "border-white/15 text-white/50 bg-white/5",
};

/** Broader area chips that expand into regions (and lat bands on the API). */
export const AREA_GROUPS = [
  { value: "Westchester", label: "Westchester" },
  { value: "North Bronx", label: "North Bronx" },
];

/** Lifestyle / commute filters — how people actually describe a search. */
export const LIFESTYLE_FILTERS = [
  { id: "near_transit", label: "Near train / Metro-North", param: "near_transit" },
  { id: "walk_in", label: "Walk-in / few steps", param: "walk_in" },
  { id: "pets", label: "Pet-friendly", param: "amenity" },
  { id: "no_fee", label: "No fee", param: "amenity", rentOnly: true },
  { id: "parking", label: "Parking", param: "amenity" },
  { id: "yard", label: "Yard / outdoor", param: "amenity" },
  { id: "laundry", label: "Laundry", param: "amenity" },
];

// Approximate map centroids per region (no geocoding key needed).
export const REGION_COORDS = {
  "The Bronx": [40.8448, -73.8648],
  "Manhattan": [40.7831, -73.9712],
  "Yonkers": [40.9312, -73.8988],
  "Mount Vernon": [40.9126, -73.8371],
  "New Rochelle": [40.9115, -73.7824],
  "Pelham / Eastchester": [40.9043, -73.8068],
  "White Plains": [41.0340, -73.7629],
  "Bronxville": [40.9382, -73.8321],
  "Westchester": [41.1220, -73.7949],
  "Queens": [40.7282, -73.7949],
  "Brooklyn": [40.6782, -73.9442],
  "Staten Island": [40.5795, -74.1502],
  "Rockland": [41.1489, -74.0250],
  "Putnam": [41.4265, -73.7490],
  "Orange": [41.3910, -74.3110],
  "Middletown": [41.4459, -74.4229],
  "Poughkeepsie": [41.7004, -73.9210],
};

export const BEDROOM_OPTIONS = ["Studio", "1", "2", "3", "4+"];

export const MIN_BED_CHIPS = [
  { value: "", label: "Any" },
  { value: "1", label: "1+" },
  { value: "2", label: "2+" },
  { value: "3", label: "3+" },
  { value: "4", label: "4+" },
];

export const MAX_BED_CHIPS = [
  { value: "", label: "Any" },
  { value: "2", label: "≤2" },
  { value: "3", label: "≤3" },
  { value: "4", label: "≤4" },
  { value: "5", label: "≤5" },
];

export const PROPERTY_STATUS = ["active", "pending", "sold"];

// Lamar's real headshot (provided by the agent)
export const AGENT_PHOTO =
  "https://customer-assets.emergentagent.com/job_smart-realtor-funnel/artifacts/xjls3bpy_0.jpg";

export function formatPrice(n) {
  if (n == null) return "—";
  return "$" + Number(n).toLocaleString("en-US");
}

/** Sale price or monthly rent display. */
export function formatListingPrice(n, listingType = "sale") {
  const base = formatPrice(n);
  if (listingType === "rent") return `${base}/mo`;
  return base;
}

/** Compact labels for map pins so Leaflet chips stay readable. */
export function formatMapPrice(n, listingType = "sale") {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const num = Number(n);
  if (listingType === "rent") {
    if (num >= 10_000) return `$${(num / 1000).toFixed(num % 1000 === 0 ? 0 : 1)}k`;
    return `$${Math.round(num).toLocaleString("en-US")}`;
  }
  if (num >= 1_000_000) {
    const m = num / 1_000_000;
    return `$${m.toFixed(m >= 10 ? 0 : 1)}M`;
  }
  if (num >= 1_000) return `$${Math.round(num / 1000)}k`;
  return `$${Math.round(num)}`;
}

export function joinList(v) {
  if (Array.isArray(v)) return v.join(", ");
  return v || "—";
}

// ---------------------------------------------------------------------------
// Brand & compliance (NY: salesperson must display sponsoring broker)
// To rebrand, change `name` / `mark` here — it propagates across the whole site.
// ---------------------------------------------------------------------------
export const BRAND = {
  name: "The Whyte Glove",
  mark: "THE WHYTE GLOVE",
  tagline: "Real estate, handled with the care you deserve.",
  agentName: "Lamar A. Whyte",
  agentFirst: "Lamar",
  title: "REALTOR®",
  titleLong: "REALTOR® · Licensed Real Estate Salesperson",
  broker: "Real Broker NY LLC",
  address: "401 Park Ave South, 10th Fl, New York, NY",
  license: "10401359055",
  phoneDirect: "718-200-4706",
  phoneOffice: "855-450-0442",
};

// Real client testimonials ONLY — never invent reviews. Add entries as
// { quote, name, context } (e.g. context: "Sold in Mott Haven"). The landing
// section stays hidden until at least one real testimonial is added.
export const TESTIMONIALS = [];

// Preferred mortgage lender (pre-approval handoff).
// Drop Jeff's headshot URL into `photo` when available (public https URL).
export const LENDER = {
  name: "Jeffrey Volcy",
  firstName: "Jeffrey",
  title: "Loan Consultant",
  company: "loanDepot",
  titleLong: "Loan Consultant · loanDepot",
  nmls: "1438279",
  url: "https://www.loandepot.com/loan-officers/jvolcy",
  photo: "/jeff-volcy.jpg",
  blurb:
    "Jeffrey partners with The Whyte Glove so buyers can get clear numbers fast — pre-approval, rate options, and a path to closing without the runaround.",
  specialties: [
    "Purchase pre-approvals",
    "First-time buyers",
    "Refinance & cash-out",
    "Conventional · FHA · VA",
  ],
};

// OneKey IDX (Matrix IDX Framing) embed.
// Paste the iframe / <script> embed code OneKey generates after you enable IDX.
// Leave as empty string to show the "coming soon" placeholder.
export const IDX_EMBED_HTML =
  '<iframe src="https://matrix-new.onekeymlsny.com/Matrix/public/IDX.aspx?idx=cb162d0a" style="width:100%;height:100%;border:0;" frameborder="0" marginwidth="0" marginheight="0" title="OneKey MLS Search"></iframe>';
